/**
 * Shared ArcGIS REST helpers.
 *
 * These were previously duplicated in App.tsx and MapView.tsx; consolidated
 * here so URL normalisation and extent conversion have one implementation.
 */

/**
 * The ArcGIS service URL one specific layer actually belongs to — its own
 * override (`Layer.arcgisServiceUrl`, set when it was added from a
 * different service than the module's primary one) if it has one, else the
 * module's primary `arcgisUrl`.
 *
 * Every place that builds an ArcGIS request FOR A SPECIFIC LAYER should
 * read this instead of `module.arcgisUrl`/`ctx.arcgisUrl` directly, so a
 * layer added from a second, "secondary" service keeps working alongside
 * layers from the module's primary one rather than being queried against
 * the wrong service.
 */
export function arcgisServiceUrlFor(
  layer: { arcgisServiceUrl?: string },
  module: { arcgisUrl?: string },
): string {
  return layer.arcgisServiceUrl || module.arcgisUrl || '';
}

/** Normalises an ArcGIS REST endpoint to its MapServer/FeatureServer root. */
export function getArcgisBaseUrl(arcgisUrl: string): string {
  try {
    const url = new URL(arcgisUrl.trim());
    let path = url.pathname.replace(/\/+$/, '');
    path = path.replace(/\/(export|exportImage|legend|layers|query|identify)$/i, '');
    path = path.replace(/\/\d+$/, '');
    url.pathname = path;
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return '';
  }
}

/**
 * Extracts a `token` query param from a raw ArcGIS URL — the shape a
 * resolved, token-secured "saved connection" URL carries (see
 * `serverConnectionsService.resolve`/`resolveService`, and
 * `getArcgisBaseUrl` above, which strips it: a base URL has more PATH
 * appended after it by every caller, which a trailing `?token=` would break).
 */
export function getArcgisToken(arcgisUrl: string | undefined | null): string | null {
  if (!arcgisUrl) return null;
  try {
    return new URL(arcgisUrl.trim()).searchParams.get('token');
  } catch {
    return null;
  }
}

/**
 * Appends the module's ArcGIS token (if any) to an already fully-built
 * request URL. Every ArcGIS `fetch` in the app should route its FINAL url
 * through this, right before the request — the one place a token-secured
 * connection needs anything different from a public service, since
 * `getArcgisBaseUrl` above cannot carry it through on its own.
 */
export function withArcgisToken(url: string, arcgisUrl: string | undefined | null): string {
  const token = getArcgisToken(arcgisUrl);
  if (!token) return url;
  return `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
}

/** MapServer supports `export` (raster); FeatureServer only `query` (vector). */
export function getArcgisServiceKind(arcgisUrl: string): 'MapServer' | 'FeatureServer' | null {
  if (/\/FeatureServer/i.test(arcgisUrl)) return 'FeatureServer';
  if (/\/MapServer/i.test(arcgisUrl)) return 'MapServer';
  return null;
}

/**
 * Converts an ArcGIS extent to [west, south, east, north] in WGS84.
 * Handles Web Mercator (the common case) and rejects out-of-range values —
 * services publish junk extents often enough that zooming to one throws the
 * map somewhere useless.
 */
export function getArcgisExtentBounds(extent: any): [number, number, number, number] | undefined {
  if (!extent) return undefined;

  const { xmin, ymin, xmax, ymax } = extent;
  if (![xmin, ymin, xmax, ymax].every((value) => Number.isFinite(value))) return undefined;

  const wkid = extent.spatialReference?.latestWkid ?? extent.spatialReference?.wkid;
  let bounds: [number, number, number, number] | undefined;

  if (wkid === 4326) {
    bounds = [xmin, ymin, xmax, ymax];
  } else if (wkid === 102100 || wkid === 3857 || wkid === 900913) {
    const EARTH_HALF_CIRCUMFERENCE = 20037508.34;
    const toLon = (x: number) => (x / EARTH_HALF_CIRCUMFERENCE) * 180;
    const toLat = (y: number) => {
      const lat = (y / EARTH_HALF_CIRCUMFERENCE) * 180;
      return (180 / Math.PI) * (2 * Math.atan(Math.exp((lat * Math.PI) / 180)) - Math.PI / 2);
    };
    bounds = [toLon(xmin), toLat(ymin), toLon(xmax), toLat(ymax)];
  }

  if (!bounds) return undefined;

  const [west, south, east, north] = bounds;
  const isSane =
    west >= -180 && east <= 180 && south >= -90 && north <= 90 && west < east && south < north;

  return isSane ? bounds : undefined;
}

/**
 * Converts an ArcGIS REST geometry to GeoJSON.
 *
 * Only meaningful when the query asked for `outSR=4326`, which is what the
 * identify path does — this function does not reproject, it re-shapes.
 *
 * Esri and GeoJSON disagree about ring winding order, and this result only
 * ever feeds the identify pop-up's zoom-to and selection highlight — bounds
 * and a rough highlight, not exact rendering — so rings are passed through
 * as-is rather than rewound. Esri polygons carry every ring of every part in
 * one flat list, which
 * cannot be split into separate polygons without a containment test, so they
 * become a single Polygon with holes — correct to draw and to measure a bound
 * from, which is all the pop-up needs.
 */
export function esriGeometryToGeoJson(geometry: any): GeoJSON.Geometry | undefined {
  if (!geometry || typeof geometry !== 'object') return undefined;

  const finite = (pair: any) =>
    Array.isArray(pair) && Number.isFinite(pair[0]) && Number.isFinite(pair[1]);

  if (Number.isFinite(geometry.x) && Number.isFinite(geometry.y)) {
    return { type: 'Point', coordinates: [geometry.x, geometry.y] };
  }

  if (Array.isArray(geometry.points)) {
    const points = geometry.points.filter(finite).map((pair: number[]) => [pair[0], pair[1]]);
    return points.length > 0 ? { type: 'MultiPoint', coordinates: points } : undefined;
  }

  if (Array.isArray(geometry.paths)) {
    const paths = geometry.paths
      .map((path: any[]) => (path ?? []).filter(finite).map((pair: number[]) => [pair[0], pair[1]]))
      .filter((path: number[][]) => path.length >= 2);
    if (paths.length === 0) return undefined;
    return paths.length === 1
      ? { type: 'LineString', coordinates: paths[0] }
      : { type: 'MultiLineString', coordinates: paths };
  }

  if (Array.isArray(geometry.rings)) {
    const rings = geometry.rings
      .map((ring: any[]) => (ring ?? []).filter(finite).map((pair: number[]) => [pair[0], pair[1]]))
      .filter((ring: number[][]) => ring.length >= 4);
    return rings.length > 0 ? { type: 'Polygon', coordinates: rings } : undefined;
  }

  // An envelope is a rectangle; drawing it as one is better than dropping it.
  const { xmin, ymin, xmax, ymax } = geometry;
  if ([xmin, ymin, xmax, ymax].every((value) => Number.isFinite(value))) {
    return {
      type: 'Polygon',
      coordinates: [
        [
          [xmin, ymin],
          [xmax, ymin],
          [xmax, ymax],
          [xmin, ymax],
          [xmin, ymin],
        ],
      ],
    };
  }

  return undefined;
}
